import type { FC, HTMLAttributes } from "react";

import { DrawerCloseTrigger } from "@ui/components/Drawer";
import { RoundButton } from "@ui/components/RoundButton";
import { useTranslations } from "@ui/providers/useTranslations";
import classNames from "classnames";

import { ChevronBackwardIcon } from "@jti/brand-icons/chevron-backward";
import { UiCrossIcon } from "@jti/brand-icons/ui-cross";

import styles from "../Drawer.module.scss";

type DrawerHeaderProps = HTMLAttributes<HTMLDivElement> & {
  hasBackButton?: boolean;
  backButtonLabel?: string;
};

export const DrawerHeader: FC<DrawerHeaderProps> = ({ children, hasBackButton, backButtonLabel, ...restProps }) => {
  const { __ } = useTranslations();

  return (
    <header
      className={classNames(styles.drawerHeader, hasBackButton && styles.drawerHeaderWithBackButton)}
      data-testid="drawer-header"
      {...restProps}
    >
      {hasBackButton && (
        <DrawerCloseTrigger>
          <div className={styles.drawerBackButtonWrapper}>
            <button className={styles.headerBackButton}>
              <ChevronBackwardIcon aria-hidden="true" width={14} height={14} />
              <h2>{backButtonLabel}</h2>
            </button>
          </div>
        </DrawerCloseTrigger>
      )}

      <div
        className={classNames(styles.drawerHeadline, {
          [styles.drawerHeadlineWithBackButton]: hasBackButton,
        })}
      >
        {children}
      </div>

      <DrawerCloseTrigger>
        <div
          className={classNames(styles.drawerCloseButtonWrapper, hasBackButton && styles.drawerCloseButtonHiddenMobile)}
        >
          <RoundButton icon={UiCrossIcon} data-testid="close-drawer-button" aria-label={__("common.close")} />
        </div>
      </DrawerCloseTrigger>
    </header>
  );
};

DrawerHeader.displayName = "DrawerHeader";
